/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import type { ObjectIdMap } from '@poweralpha/common';
import {
  type InfiniteData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { HTTPError } from 'ky';
import { sum } from 'lodash';
import { useCallback } from 'react';
import { getNotifications, markAllNotificationAsViewed, markNotificationAsViewed } from '../api';
import { getbnCRMProjectIdMap } from '../api/bncrm';
import { useAuth } from '../auth';
import { NOTIFICATIONS_QUERY_KEY } from './queryKeys';
import type { UINotificationsQueryResult } from './types';
import { formatNotifications } from './utils';

const PAGE_SIZE = 20; // # of notifications per page
const REFETCH_INTERVAL = 20; // secs

export const useProjectIdMap = () =>
  useQuery<ObjectIdMap>({
    queryKey: ['project-id-map'],
    queryFn: () => getbnCRMProjectIdMap(),
  }).data;

export const useInvalidateNotifications = () => {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY }),
    [queryClient],
  );
};

// The bell's feed: latest notifications (paged) plus every unread one. The
// cache key is shared with useAgentRunNotifications, which prepends rows.
export function useNotificationsFeed() {
  const { user } = useAuth();
  const projectIdMap = useProjectIdMap();
  const invalidate = useInvalidateNotifications();
  const enabled = Boolean(user) && Boolean(projectIdMap);

  const query = useInfiniteQuery<
    UINotificationsQueryResult,
    HTTPError,
    InfiniteData<UINotificationsQueryResult>,
    typeof NOTIFICATIONS_QUERY_KEY,
    number
  >({
    queryKey: NOTIFICATIONS_QUERY_KEY,
    queryFn: ({ pageParam = PAGE_SIZE }) =>
      getNotifications(PAGE_SIZE, pageParam - PAGE_SIZE).then((res) =>
        formatNotifications(res, projectIdMap, user?.email),
      ),
    initialPageParam: PAGE_SIZE,
    getNextPageParam: (_, pages) => sum(pages.map(({ rows }) => rows.length)) + PAGE_SIZE,
    enabled,
    refetchInterval: enabled ? REFETCH_INTERVAL * 1e3 : false,
  });

  const { mutate: markAsViewed } = useMutation<void, HTTPError, string>({
    mutationFn: markNotificationAsViewed,
    onSuccess: invalidate,
  });

  const { mutate: markAllAsViewed } = useMutation<{ count: number }>({
    mutationFn: markAllNotificationAsViewed,
    onSuccess: invalidate,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    fetchNextPage: query.fetchNextPage,
    markAsViewed,
    markAllAsViewed: () => markAllAsViewed(),
  };
}
